import { useAuthStore } from '@/stores/useAuthStore';
import { Lang } from '@/types/user';
import i18n from 'i18next';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

export const useLanguage = () => {
  const supportedLngs = useMemo(() => ['nl', 'fr', 'en', 'de'], []);
  const { lang } = useParams<{ lang: Lang }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, updateAuthUserLang } = useAuthStore();

  // Get current search parameters as a string
  const searchString = useMemo(() => {
    return searchParams.toString() ? `?${searchParams.toString()}` : '';
  }, [searchParams]);

  const defaultLang = useMemo(() => user?.lang ?? 'fr', [user?.lang]);

  // Apply the language to document and i18n when lang param changes
  useEffect(() => {
    if (lang && supportedLngs.includes(lang) && lang !== i18n.language) {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
    }
  }, [lang, supportedLngs]);

  const changeLanguage = useCallback(
    async (new_lang: Lang) => {
      if (!supportedLngs.includes(new_lang)) {
        const fallbackLang = user?.lang || defaultLang;
        navigate(`/${fallbackLang}/dashboard${searchString}`, {
          replace: true,
        });
        return;
      }

      if (i18n.language === new_lang && lang === i18n.language) return;

      // Update i18n and document lang
      await i18n.changeLanguage(new_lang);
      document.documentElement.lang = new_lang;

      // Update URLs
      const current_path = location.pathname;
      if (current_path === '/' || !supportedLngs.includes(lang ?? '')) {
        navigate(`/${new_lang}${searchString}`, { replace: true });
      } else {
        const new_path = current_path.replace(
          /^\/(nl|fr|en|de)/,
          `/${new_lang}`,
        );
        navigate(`${new_path}${searchString}`, { replace: true });
      }

      // Update user preference if needed
      if (user && (user.lang !== i18n.language || lang !== i18n.language)) {
        updateAuthUserLang(new_lang);
      }
    },
    [
      lang,
      location.pathname,
      navigate,
      user,
      updateAuthUserLang,
      defaultLang,
      supportedLngs,
      searchString,
    ],
  );

  /**
   * Initialize the app language
   * This function is called when the app first loads
   * It detects the current URL language, user preference, or default
   */
  const initLanguage = useCallback(async () => {
    const currentPath = location.pathname;
    const langFromPath = currentPath.match(/^\/(nl|fr|en|de)/)?.[1] as
      | Lang
      | undefined;

    // No language in URL, redirect to proper URL with language
    if (!langFromPath) {
      // Choose language in this order:
      // 1. User preference if logged in
      // 2. Browser language if supported
      // 3. Default language (fr)
      let targetLang: Lang;

      if (user?.lang && supportedLngs.includes(user.lang)) {
        targetLang = user.lang;
      } else {
        const browserLang = navigator.language.split('-')[0] as Lang;
        targetLang = supportedLngs.includes(browserLang)
          ? browserLang
          : defaultLang;
      }

      // Redirect to same path but with language prefix
      const pathWithoutLeadingSlash = currentPath.startsWith('/')
        ? currentPath.substring(1)
        : currentPath;

      const targetPath =
        `/${targetLang}/${pathWithoutLeadingSlash}${searchString}`.replace(
          '//',
          '/',
        );

      // Set i18n lang before navigating to avoid potential flicker/mismatch
      if (targetLang !== i18n.language) {
        await i18n.changeLanguage(targetLang);
        document.documentElement.lang = targetLang;
      }

      navigate(targetPath, { replace: true });
      return;
    }

    // Language is present in the URL (`langFromPath`)
    // Ensure i18n instance and document lang match the URL.
    // We no longer force a change based on user.lang here,
    // as changeLanguage handles updating user.lang preference when the user explicitly selects a language.
    if (langFromPath !== i18n.language) {
      await i18n.changeLanguage(langFromPath);
      document.documentElement.lang = langFromPath;
    }
  }, [
    location.pathname,
    user, // Keep user to check preference for initial redirect
    defaultLang, // Keep defaultLang for fallback
    navigate,
    searchString,
    supportedLngs,
  ]);

  return { changeLanguage, initLanguage };
};
